/* All articles only. Fresh shuffle on reload; pagination keeps one permutation.
 * Functional session storage contains article IDs only, not visitor identifiers.
 * Native HTML/pagination remains usable without JS or when a request fails.
 */
(() => {
  const KEY = 'sd-journal-shuffle-v1';
  const PAGE_SIZE = 12;
  const ENDPOINT = 'https://sawasdeal-journal-counter.zlota-cn.workers.dev';
  function stored() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch { return null; }
  }
  function save(value) {
    try { sessionStorage.setItem(KEY, JSON.stringify(value)); } catch { /* Still shuffle without storage. */ }
  }
  function shuffled(ids, previousFirst, offset) {
    const result = ids.slice();
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    if (result.length > 1 && result[offset] === previousFirst) {
      const other = offset === result.length - 1 ? 0 : offset + 1;
      [result[offset], result[other]] = [result[other], result[offset]];
    }
    return result;
  }
  async function refreshDisplayedCounts(cards) {
    // Public aggregate delivery only. No read event, consent bypass or metric changes.
    const stats = cards.flatMap(card => [...card.querySelectorAll('[data-sd-views-id]')]);
    const ids = [...new Set(stats.map(node => node.dataset.sdViewsId))];
    for (let i = 0; i < ids.length; i += 40) {
      try {
        const response = await fetch(ENDPOINT + '/counts?ids=' + encodeURIComponent(ids.slice(i, i + 40).join(',')), {
          credentials: 'omit', mode: 'cors', referrerPolicy: 'no-referrer'
        });
        if (!response.ok) continue;
        const data = await response.json();
        for (const node of stats) {
          const value = data.counts?.[node.dataset.sdViewsId];
          if (!Number.isSafeInteger(value) || value < 0) continue;
          const number = node.querySelector('[data-sd-view-number]');
          if (number) { number.textContent = value.toLocaleString('en-US'); node.hidden = false; }
        }
      } catch { /* Do not fabricate unavailable totals. */ }
    }
  }
  async function start(fromBackForwardCache = false) {
    const grid = document.querySelector('[data-journal-shuffle="true"]');
    if (!grid || grid.dataset.journalShuffleState === 'loading') return;
    const count = Number(grid.dataset.journalCount), page = Number(grid.dataset.journalPage);
    const pageCount = Math.ceil(count / PAGE_SIZE), offset = (page - 1) * PAGE_SIZE;
    if (!Number.isInteger(count) || count < 1 || !Number.isInteger(page) || page < 1 || page > pageCount) return;
    const host = grid.closest('[data-journal-section]');
    if (host.dataset.shuffleFallback === 'true') return;
    const navigation = performance.getEntriesByType('navigation')[0]?.type;
    const old = stored();
    const path = location.pathname;
    const carry = old?.path === path && (fromBackForwardCache || navigation === 'back_forward' ||
      (navigation !== 'reload' && old.pendingPage === page && Date.now() - old.pendingAt < 60000));
    const alreadyShuffled = grid.dataset.journalShuffleState === 'ready';
    grid.dataset.journalShuffleState = 'loading';
    grid.setAttribute('aria-busy', 'true');
    try {
      const pool = host.querySelector('[data-journal-shuffle-pool]');
      const embedded = pool ? [...pool.content.querySelectorAll('.sd-journal__card')] : [];
      const completePool = embedded.length === count && new Set(embedded.map(card => card.dataset.articleId)).size === count;
      let cards;
      if (completePool) {
        // Synchronous: select the final order before revealing any native cards.
        // Clone so back/forward restores retain the complete inert pool.
        cards = embedded.map(card => card.cloneNode(true));
        grid.dataset.journalShuffleSource = 'embedded';
      } else {
      const requests = Array.from({ length: pageCount }, (_, index) => index + 1);
      const pages = await Promise.all(requests.map(async nativePage => {
        if (nativePage === page && !alreadyShuffled) return [...grid.querySelectorAll('.sd-journal__card')];
        const url = new URL(location.href);
        url.searchParams.set('page', String(nativePage));
        url.searchParams.set('section_id', host.dataset.journalSection);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Article page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...doc.querySelectorAll('.sd-journal__card')];
      }));
      cards = pages.flat();
      grid.dataset.journalShuffleSource = 'fetched';
      }
      // A timed-out request must never reorder already revealed fallback content.
      if (host.dataset.shuffleFallback === 'true') return;
      const byId = new Map(cards.map(card => [card.dataset.articleId, card]));
      if (cards.length !== count || byId.size !== count || byId.has(undefined)) throw new Error('Incomplete article set');
      const ids = [...byId.keys()].sort();
      const validPrevious = Array.isArray(old?.order) && old.order.length === count && new Set(old.order).size === count && old.order.every(id => byId.has(id));
      const firstByPage = old?.path === path ? { ...old.firstByPage } : {};
      const order = carry && validPrevious ? old.order : shuffled(ids, firstByPage[page], offset);
      const selected = order.slice(offset, offset + PAGE_SIZE).map(id => byId.get(id));
      firstByPage[page] = selected[0].dataset.articleId;
      save({ path, order, firstByPage, pendingPage: null, pendingAt: 0 });
      grid.replaceChildren(...selected.map(card => document.adoptNode(card)));
      grid.dataset.journalShuffleState = 'ready';
      grid.dispatchEvent(new CustomEvent('journal:articles-reordered', { bubbles: true }));
      void refreshDisplayedCounts(selected);
      if (!host.dataset.shufflePaginationBound) {
        host.dataset.shufflePaginationBound = 'true';
        host.addEventListener('click', event => {
          const link = event.target.closest('.pagination-wrapper a[href]');
          if (!link || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const url = new URL(link.href, location.href);
          if (url.origin !== location.origin || url.pathname !== path) return;
          const nextPage = Number(url.searchParams.get('page') || '1');
          const latest = stored();
          if (latest?.path === path && Number.isInteger(nextPage) && nextPage > 0 && nextPage <= pageCount) {
            save({ ...latest, pendingPage: nextPage, pendingAt: Date.now() });
          }
        });
      }
    } catch {
      grid.dataset.journalShuffleState = 'unavailable';
      // Preserve native cards, article links, counts and pagination on failure.
    } finally {
      grid.removeAttribute('aria-busy');
      window.clearTimeout(window.sdJournalShuffleRevealTimer);
    }
  }
  void start();
  window.addEventListener('pageshow', event => { if (event.persisted) void start(true); });
})();
